import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

export const useUserManagement = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [userProducts, setUserProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [filterVerified, setFilterVerified] = useState<boolean | null>(null);
  const [filterType, setFilterType] = useState<string | null>(null);
  const itemsPerPage = 10;

  const hardcodedAdminId = "83b55862-d096-44b4-b0af-4f59e29d8297";
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<boolean>(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          if (user.id === hardcodedAdminId) {
            setIsAdmin(true);
            fetchUsers();
            return;
          }
          
          try {
            const { data, error } = await supabase
              .from('user_roles')
              .select('*')
              .eq('user_id', user.id)
              .eq('role', 'admin')
              .maybeSingle();
            
            if (error) {
              if (error.message?.includes('infinite recursion')) {
                console.log('Ignoring recursion error, using alternative method');
              } else {
                throw error;
              }
            } else if (data) {
              setIsAdmin(true);
              fetchUsers();
            }
          } catch (innerError) {
            console.warn('Fallback method also failed:', innerError);
          }
        }
      } catch (error) {
        console.error('Erreur de vérification du statut admin', error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const startIndex = (currentPage - 1) * itemsPerPage;
      
      let query = supabase
        .from('profiles')
        .select('*', { count: 'exact' });
        
      if (searchQuery) {
        query = query.or(`email.ilike.%${searchQuery}%,full_name.ilike.%${searchQuery}%`);
      }
      
      if (filterVerified !== null) {
        query = query.eq('verified', filterVerified);
      }
      
      if (filterType) {
        query = query.eq('type', filterType);
      }
      
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });
      
      query = query.range(startIndex, startIndex + itemsPerPage - 1);
      
      const { data, error, count } = await query;
      
      if (error) throw error;
      
      if (data) {
        setUsers(data);
        
        if (count) {
          setTotalPages(Math.ceil(count / itemsPerPage));
        }
      }
    } catch (error) {
      console.error('Erreur de chargement des utilisateurs', error);
      toast.error("Erreur de chargement des utilisateurs", {
        description: "Impossible de récupérer la liste des utilisateurs.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, currentPage, sortBy, sortOrder, searchQuery, filterVerified, filterType]);

  // This function will open the delete confirmation dialog
  const confirmDeleteUser = (userId: string) => {
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  // This function handles the actual user deletion process
  const handleDeleteUser = async (userId: string) => {
    if (!isAdmin) {
      toast.error("Accès refusé", {
        description: "Vous n'avez pas les permissions requises.",
      });
      return;
    }

    if (userId === hardcodedAdminId) {
      toast.error("Action non autorisée", {
        description: "L'administrateur principal ne peut pas être supprimé.",
      });
      return;
    }

    setDeletingUser(true);

    try {
      // First, delete user's related data
      await Promise.all([
        // Delete relationships
        supabase.from('professional_patients').delete().or(`professional_id.eq.${userId},patient_id.eq.${userId}`),
        supabase.from('professional_patient_access').delete().or(`professional_id.eq.${userId},patient_id.eq.${userId}`),
        supabase.from('professional_appointments').delete().or(`professional_id.eq.${userId},patient_id.eq.${userId}`),
        
        // Delete messages
        supabase.from('messages').delete().or(`sender_id.eq.${userId},receiver_id.eq.${userId}`),
        
        // Delete product-related data
        supabase.from('professional_products').delete().eq('professional_id', userId),
        
        // Delete services
        supabase.from('professional_services').delete().eq('professional_id', userId),
        
        // Remove admin role
        supabase.rpc('remove_admin_role', { user_id_param: userId }),
      ]);

      // Delete profile record
      await supabase.from('profiles').delete().eq('id', userId);

      // Delete the actual user from auth
      const { error } = await supabase.functions.invoke('delete-user', {
        body: { userId }
      });

      if (error) throw error;

      // Update local state
      setUsers(users.filter(user => user.id !== userId));

      toast.success("Utilisateur supprimé", {
        description: "L'utilisateur et toutes ses données ont été supprimés avec succès.",
      });
      
      if (userDetailsOpen && selectedUser?.id === userId) {
        setUserDetailsOpen(false);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      toast.error("Erreur", {
        description: "Impossible de supprimer l'utilisateur. Veuillez réessayer.",
      });
    } finally {
      setDeletingUser(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const toggleAdminRole = async (userId: string, isCurrentlyAdmin: boolean) => {
    if (!isAdmin) {
      toast.error("Accès refusé", {
        description: "Vous n'avez pas les permissions requises.",
      });
      return;
    }

    try {
      if (isCurrentlyAdmin) {
        const { error } = await supabase.rpc('remove_admin_role', { user_id_param: userId });
        if (error) throw error;
        
        toast.success("Rôle admin retiré", {
          description: "Le rôle admin a été retiré avec succès.",
        });
      } else {
        const { error } = await supabase.rpc('add_admin_role', { user_id_param: userId });
        if (error) throw error;
        
        toast.success("Rôle admin ajouté", {
          description: "Le rôle admin a été ajouté avec succès.",
        });
      }
      
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la modification du rôle admin', error);
      toast.error("Erreur", {
        description: "Impossible de modifier le rôle admin.",
      });
    }
  };

  const toggleVerifiedStatus = async (userId: string, isCurrentlyVerified: boolean) => {
    if (!isAdmin) {
      toast.error("Accès refusé", {
        description: "Vous n'avez pas les permissions requises.",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ verified: !isCurrentlyVerified })
        .eq('id', userId);
        
      if (error) throw error;
      
      setUsers(users.map(user => 
        user.id === userId ? { ...user, verified: !isCurrentlyVerified } : user
      ));
      
      toast.success(isCurrentlyVerified ? "Badge de vérification retiré" : "Utilisateur vérifié", {
        description: isCurrentlyVerified 
          ? "Le badge de vérification a été retiré avec succès." 
          : "L'utilisateur a été vérifié et a reçu un badge.",
      });
    } catch (error) {
      console.error('Erreur lors de la modification du statut de vérification', error);
      toast.error("Erreur", {
        description: "Impossible de modifier le statut de vérification.",
      });
    }
  };

  const toggleBlockedStatus = async (userId: string, isCurrentlyBlocked: boolean) => {
    if (!isAdmin) {
      toast.error("Accès refusé", {
        description: "Vous n'avez pas les permissions requises.",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ blocked: !isCurrentlyBlocked })
        .eq('id', userId);
        
      if (error) throw error;
      
      setUsers(users.map(user => 
        user.id === userId ? { ...user, blocked: !isCurrentlyBlocked } : user
      ));
      
      toast.success(isCurrentlyBlocked ? "Compte débloqué" : "Compte bloqué", {
        description: isCurrentlyBlocked 
          ? "Le compte a été débloqué avec succès." 
          : "Le compte a été bloqué avec succès.",
      });
    } catch (error) {
      console.error('Erreur lors de la modification du statut de blocage', error);
      toast.error("Erreur", {
        description: "Impossible de modifier le statut de blocage.",
      });
    }
  };

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();
        
      if (error) {
        console.error('Erreur de vérification du statut admin', error);
        return false;
      }
      
      return !!data;
    } catch (error) {
      console.error('Erreur de vérification du statut admin', error);
      return false;
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const fetchUserProducts = async (userId: string) => {
    setLoadingProducts(true);
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('type')
        .eq('id', userId)
        .single();
        
      if (profileData?.type === 'professional') {
        const { data: products, error } = await supabase
          .from('professional_products')
          .select('*')
          .eq('professional_id', userId);
          
        if (error) throw error;
        
        setUserProducts(products || []);
      } else {
        setUserProducts([]);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des produits', error);
      toast.error("Erreur", {
        description: "Impossible de récupérer les produits de l'utilisateur.",
      });
      setUserProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const openUserDetails = (user: any) => {
    setSelectedUser(user);
    fetchUserProducts(user.id);
    setUserDetailsOpen(true);
  };

  const openEditUser = (user: any) => {
    setSelectedUser(user);
    setEditUserOpen(true);
  };

  const updateUserProfile = async (updatedData: any) => {
    if (!selectedUser) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updatedData)
        .eq('id', selectedUser.id);
        
      if (error) throw error;
      
      setUsers(users.map(user => 
        user.id === selectedUser.id ? { ...user, ...updatedData } : user
      ));
      
      toast.success("Profil mis à jour", {
        description: "Les informations du profil ont été mises à jour avec succès.",
      });
      
      setEditUserOpen(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil', error);
      toast.error("Erreur", {
        description: "Impossible de mettre à jour le profil.",
      });
    }
  };

  const deleteUserProduct = async (productId: string) => {
    try {
      const { error } = await supabase
        .from('professional_products')
        .delete()
        .eq('id', productId);
        
      if (error) throw error;
      
      setUserProducts(userProducts.filter(product => product.id !== productId));
      
      toast.success("Produit supprimé", {
        description: "Le produit a été supprimé avec succès.",
      });
    } catch (error) {
      console.error('Erreur lors de la suppression du produit', error);
      toast.error("Erreur", {
        description: "Impossible de supprimer le produit.",
      });
    }
  };

  return {
    users,
    loading,
    isAdmin,
    currentPage,
    totalPages,
    searchQuery,
    sortBy,
    sortOrder,
    filterVerified,
    filterType,
    selectedUser,
    userDetailsOpen,
    editUserOpen,
    userProducts,
    loadingProducts,
    hardcodedAdminId,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setFilterVerified,
    setFilterType,
    setCurrentPage,
    confirmDeleteUser,
    handleDeleteUser,
    toggleAdminRole,
    toggleVerifiedStatus,
    toggleBlockedStatus,
    checkAdminStatus,
    handleSort,
    openUserDetails,
    openEditUser,
    updateUserProfile,
    deleteUserProduct,
    setUserDetailsOpen,
    setEditUserOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    userToDelete,
    setUserToDelete,
    deletingUser
  };
};
